import { readAltitudeMeters } from '@fit-editor/fit-core'
import type { ActivityRecord, Operation, TerrainSource } from '@fit-editor/fit-core'

import type { TimeRangeMs } from '@/store/activity-store'

/** Presentation/UI helpers for the terrain-elevation feature. Pure -- no worker, no DOM. */

export function hasAnyGps(records: ActivityRecord[]): boolean {
  return records.some(
    (r) => typeof r.positionLat === 'number' && typeof r.positionLong === 'number',
  )
}

/** Device elevation with fit-core's `enhancedAltitude`-first precedence. */
export function readDeviceAltitude(record: ActivityRecord): number | undefined {
  return readAltitudeMeters(record)
}

export function hasAnyDeviceAltitude(records: ActivityRecord[]): boolean {
  return records.some((r) => readDeviceAltitude(r) != null)
}

export type SetElevationOperation = Extract<Operation, { type: 'setElevation' }>
export type ElevationMode = SetElevationOperation['mode']

export interface SetElevationPlan {
  /** `null` when there is nothing to apply (no terrain values in scope). */
  operation: SetElevationOperation | null
  /** Records in scope (with a timestamp) that will receive a terrain value, before fill/replace filtering. */
  sampleCount: number
  /** Records in scope (with a timestamp) with no terrain value -- left unchanged. */
  skippedCount: number
}

/**
 * Builds the `setElevation` operation from the *already-loaded* overlay
 * (no network request): one `{ timestampMs, elevationM }` sample per
 * in-scope record that has a timestamp and a terrain value. Records whose
 * terrain value is `null` (no GPS, or no tile coverage) are skipped and
 * counted, never filled with a guess. `range === null` means the whole
 * activity (the op then carries no `start`/`end`).
 */
export function buildSetElevationPlan(args: {
  records: ActivityRecord[]
  terrainValues: (number | null)[]
  range: TimeRangeMs | null
  mode: ElevationMode
  source: TerrainSource
}): SetElevationPlan {
  const { records, terrainValues, range, mode, source } = args
  const samples: SetElevationOperation['samples'] = []
  let skippedCount = 0
  for (let i = 0; i < records.length; i++) {
    const timestamp = records[i]?.timestamp
    if (!(timestamp instanceof Date)) continue
    const timestampMs = timestamp.getTime()
    if (range != null && (timestampMs < range.start || timestampMs > range.end)) continue
    const value = terrainValues[i]
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      skippedCount++
      continue
    }
    samples.push({ timestampMs, elevationM: value })
  }
  const operation: SetElevationOperation | null =
    samples.length === 0
      ? null
      : {
          type: 'setElevation',
          ...(range != null ? { start: new Date(range.start), end: new Date(range.end) } : {}),
          mode,
          samples,
          source,
        }
  return { operation, sampleCount: samples.length, skippedCount }
}

/** Index-aligned count of records whose device elevation differs between `before` and `after`. */
export function countElevationChanges(before: ActivityRecord[], after: ActivityRecord[]): number {
  let count = 0
  const n = Math.min(before.length, after.length)
  for (let i = 0; i < n; i++) {
    const a = before[i]
    const b = after[i]
    if (a == null || b == null) continue
    if (readDeviceAltitude(a) !== readDeviceAltitude(b)) count++
  }
  return count
}

export function describeTerrainResolution(source: TerrainSource): string {
  return source.resolutionM >= 30
    ? '30 m Copernicus GLO-30'
    : `high-resolution national DEM (≤${source.resolutionM} m)`
}
